'use client';

import { useState, useCallback, useEffect, useMemo, PropsWithChildren } from 'react';
import { Alert, type AlertProps } from '@tuic/web-compositions/core/Alert';
import { CarouselDefaultPagination, CarouselStage, UniversalCarousel } from '@tuic/web-compositions/shared/UniversalCarousel';
import { ComponentBlock } from '@modules/prism-client/types/types';
import PrismAPI from '@modules/prism-client/types/prism-types';
import { WithAlertDismiss } from './WithAlertDismiss';
import { getI18nForPagination } from '@lib/i18n-sets';
import { useClientTranslation } from '@lib/i18n/client';
import { createAlertClientStorage } from '@modules/prism-client/lib/alerts/storage';
import { createClientAlertFilter } from '@modules/prism-client/lib/alerts/filters';
import { DateTime } from 'luxon';
export type AlertCollectionData = {
  alerts: Array<AlertProps & {
    id?: string;
    dismissible?: boolean;
    dismissAction?: PrismAPI.ActionDefinition;
    resetAt?: string;
  }>;
  origin: string; // Page context for isolating dismissed alerts
};
const AlertCollectionWrapper = ({
  children
}: PropsWithChildren) => {
  return <div data-block-type="alertCollection" hidden={children ? undefined : true} data-sentry-component="AlertCollectionWrapper" data-sentry-source-file="AlertCollectionBlock.tsx">
      {children}
    </div>;
};
export const AlertCollectionBlock: ComponentBlock<AlertCollectionData> = ({
  data,
  fallback
}) => {
  const {
    t
  } = useClientTranslation();
  const [hiddenAlerts, setHiddenAlerts] = useState<string[]>();

  // Instantiate the alert storage client
  const alertStorage = useMemo(() => createAlertClientStorage(), []);

  // Create a reset dates collection from alerts
  const resetDates = useMemo(() => {
    const dates: Record<string, string> = {};
    if (data?.alerts) {
      data.alerts.forEach(alert => {
        if (alert.resetAt) {
          dates[alert.id || ''] = alert.resetAt;
        }
      });
    }
    return dates;
  }, [data?.alerts]);
  const handleDismiss = useCallback((alertId: string) => {
    // Hide the alert immediately by adding it to the hidden alerts state
    setHiddenAlerts(prev => [...(prev ?? []), alertId]);

    // Save the dismissed alert in the client storage
    if (data?.origin) {
      alertStorage.save({
        id: alertId,
        origin: data.origin,
        dismissedAt: DateTime.now()
      });
    }
  }, [alertStorage, data?.origin]);
  useEffect(() => {
    // If hiddenAlerts is already defined, we don't need to fetch the alerts from storage again
    if (typeof hiddenAlerts !== 'undefined' || !data?.origin) {
      return;
    }

    // Fetch the dismissed alerts from storage
    alertStorage.get().then(clientAlerts => {
      // Create a filter function to filter out alerts that have been dismissed
      const filter = createClientAlertFilter({
        resetCollection: resetDates
      });
      const alertsFromStorage = clientAlerts
      // Filter the alerts based on the origin -- this is important to ensure that we only show alerts that are relevant to the current context
      .filter(alert => alert.origin === data.origin)
      // Filter out alerts that have been dismissed -- we negate the filter function to get the alerts that should be hidden
      .filter(alert => !filter(alert))
      // We only need the IDs here
      .map(({
        id
      }) => id);
      setHiddenAlerts(alertsFromStorage);
    });
  }, [alertStorage, data?.origin, hiddenAlerts, resetDates]);
  if (fallback) {
    // We do return null in fallback because we do not have information if alerts are there or not on the initial render
    return <AlertCollectionWrapper />;
  }
  if (!data || !data.alerts || data.alerts.length === 0) {
    return <AlertCollectionWrapper />;
  }

  // Don't render alerts until we've checked localStorage to prevent flicker
  // If hiddenAlerts is undefined, this means we are in the initial loading state
  if (typeof hiddenAlerts === 'undefined') {
    return <AlertCollectionWrapper />;
  }
  const {
    alerts
  } = data;

  // Filter out dismissed alerts
  const visibleAlerts = alerts.filter(alert => !alert.id || !(hiddenAlerts ?? []).includes(alert.id));
  if (visibleAlerts.length === 0) {
    return <AlertCollectionWrapper />;
  }
  const renderAlert = (alert: (typeof visibleAlerts)[0], index: number) => {
    const alertId = alert.id || `alert-${index}`;
    const {
      dismissAction,
      dismissible,
      onClose,
      ...alertProps
    } = alert;

    // Show close button if alert is dismissible
    const shouldShowClose = dismissible;

    // For non-account users without dismissAction, handle dismissal directly
    const handleAlertClose = shouldShowClose && !dismissAction && alert.id ? () => handleDismiss(alert.id!) : undefined;
    return <WithAlertDismiss key={alertId} id={alert.id} dismissAction={dismissAction} onDismiss={handleDismiss} data-sentry-element="WithAlertDismiss" data-sentry-component="renderAlert" data-sentry-source-file="AlertCollectionBlock.tsx">
        {({
        onClose: dismissHandler
      }) => <Alert {...alertProps} onClose={shouldShowClose ? dismissHandler || handleAlertClose : undefined} />}
      </WithAlertDismiss>;
  };
  return <AlertCollectionWrapper data-sentry-element="AlertCollectionWrapper" data-sentry-component="AlertCollectionBlock" data-sentry-source-file="AlertCollectionBlock.tsx">
      {visibleAlerts.length === 1 ?
    // Render a single alert without a carousel
    renderAlert(visibleAlerts[0], 0) :
    // Render multiple alerts in a carousel
    <UniversalCarousel>
          <CarouselStage className="px-4 carousel-stage-gap-2 md:carousel-stage-gap-6 -mx-4" itemProps={{
        className: 'scroll-mx-6'
      }}>
            {visibleAlerts.map((alert, index) => renderAlert(alert, index))}
          </CarouselStage>
          <CarouselDefaultPagination i18n={getI18nForPagination(t)} />
        </UniversalCarousel>}
    </AlertCollectionWrapper>;
};